(() => {
  if (customElements.get('frontrow-widget')) return;
  const origin = 'https://app.thefrontrowhealth.com';
  const scripts = new Map();
  // Serialize vendor scripts: each presentation registers globals/shared modal helpers.
  let queue = Promise.resolve();
  // Badges emit this before the lazy qual script has registered its listener.
  window.addEventListener('message', (event) => {
    if (event.origin !== origin || event.data !== 'SCROLL_TO_TESTIMONIALS') return;
    const source = [...document.querySelectorAll('frontrow-widget iframe')]
      .find((frame) => frame.contentWindow === event.source);
    if (!source) return;
    const reviews = document.querySelector('frontrow-widget[data-presentation="qual"]');
    if (!reviews || reviews.dataset.productId !== source.closest('frontrow-widget').dataset.productId) return;
    reviews.startLoading?.();
    reviews.classList.add('is-visible');
    // Once ready, the vendor handles scrolling and its own click analytics.
    if (!reviews.vendorReady) reviews.scrollIntoView({
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start'
    });
  });
  function loadScript(type, id) {
    const src = `${origin}/api/widgets/script?presentation_type=${type}&product_id=${id}`;
    if (!scripts.has(src)) {
      const job = queue.then(() => new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.append(script);
      }));
      scripts.set(src, job);
      queue = job.catch(() => {});
    }
    return scripts.get(src);
  }
  class FrontrowWidget extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      // Run after the document and theme blocks have finished parsing.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.mount(), { once: true });
      } else this.mount();
    }
    mount() {
      const type = this.dataset.presentation;
      const duplicates = [...document.querySelectorAll(`frontrow-widget[data-presentation="${type}"]`)];
      if (duplicates[0] !== this) { this.remove(); return; }
      const frame = this.querySelector('iframe');
      if (!frame) return;
      if (type === 'qual') {
        const faq = document.querySelector('#MainContent .faq-pdp');
        const stamped = document.querySelector('#MainContent .knn-stamped-main-widget-section');
        if (faq) {
          (faq.closest('.shopify-section') || faq).after(this);
        } else if (stamped) {
          stamped.before(this);
        } else { this.remove(); return; }
        this.hidden = false;
      }
      const start = () => {
        if (this.started) return;
        this.started = true;
        this.intersection?.disconnect();
        if (type === 'qual') {
          this.setAttribute('aria-busy', 'true');
          const status = this.querySelector('[data-frontrow-status]');
          if (status) status.hidden = false;
          this.loadingTimer = setTimeout(() => {
            if (status) status.textContent = 'Reviews are taking longer to load. Please check your connection or refresh the page.';
          }, 20000);
          this.onMessage = (event) => {
            if (event.origin !== origin || event.source !== frame.contentWindow ||
                event.data?.name !== 'RESIZE_TESTIMONIALS_HEIGHT' || !(Number(event.data.value) > 0)) return;
            clearTimeout(this.loadingTimer);
            this.setAttribute('aria-busy', 'false');
            if (status) status.hidden = true;
          };
          window.addEventListener('message', this.onMessage);
        }
        // Assign src only after positioning; moving a live iframe reloads it.
        if (frame.dataset.src) frame.src = frame.dataset.src;
        if (type !== 'qual' || !('IntersectionObserver' in window)) this.classList.add('is-visible');
        loadScript(type, this.dataset.productId).then(() => {
          this.vendorReady = true;
          frame.contentWindow?.postMessage({ name: 'PARENT_READY' }, origin);
        }).catch(() => {
          if (type !== 'qual') { this.loadFailed = true; this.hidden = true; return; }
          clearTimeout(this.loadingTimer);
          this.setAttribute('aria-busy', 'false');
          const status = this.querySelector('[data-frontrow-status]');
          if (status) {
            status.hidden = false;
            status.textContent = 'Unable to load clinician reviews. Please refresh the page to try again.';
          }
        });
      };
      this.startLoading = start;
      if (type === 'sticker') {
        const gallery = this.closest('.product-information__media')?.querySelector('media-gallery');
        if (!gallery) { this.remove(); return; }
        const productMedia = new Set((gallery.dataset.frontrowProductMedia || '').split(/\s+/).filter(Boolean));
        // Position once, before src is assigned. Reparenting a live iframe reloads it.
        gallery.classList.add('frontrow-media-host');
        gallery.append(this);
        const isProductPhoto = (host) => {
          const media = host?.querySelector('.product-media[data-media-id]');
          return media && productMedia.has(media.dataset.mediaId);
        };
        const firstProductPhoto = () => {
          const grid = gallery.querySelector('.media-gallery__grid');
          const gridVisible = grid && getComputedStyle(grid).display !== 'none';
          const candidates = gridVisible
            ? [...grid.children]
            : [...gallery.querySelectorAll('slideshow-slide')];
          return candidates.find(isProductPhoto);
        };
        const place = () => {
          const grid = gallery.querySelector('.media-gallery__grid');
          const gridVisible = grid && getComputedStyle(grid).display !== 'none';
          const slide = gallery.querySelector('slideshow-slide[aria-hidden="false"]') ||
            gallery.querySelector('slideshow-slide:not([aria-hidden])');
          // A grid has no active slide: keep its single badge on the first product photo.
          const photo = gridVisible ? [...grid.children].find(isProductPhoto) : slide;
          // The badge belongs to the one-bottle hero image, not every carousel image.
          const isFirstProductPhoto = photo === firstProductPhoto();
          const host = gridVisible ? photo : gallery.querySelector('slideshow-slides');
          this.hidden = !host || !isProductPhoto(photo) || !isFirstProductPhoto || this.loadFailed === true;
          if (this.hidden) return;
          const bounds = host.getBoundingClientRect();
          const galleryBounds = gallery.getBoundingClientRect();
          this.style.top = `${bounds.top - galleryBounds.top + 8}px`;
          this.style.left = `${bounds.left - galleryBounds.left}px`;
          this.style.width = `${bounds.width}px`;
          // Preserve the vendor's fixed artwork ratio on small galleries.
          const scale = Math.min(1, host.clientWidth / 437);
          this.style.setProperty('--frontrow-sticker-scale', Math.max(0.1, scale));
        };
        const schedulePlace = () => {
          if (this.positionFrame) return;
          this.positionFrame = requestAnimationFrame(() => {
            this.positionFrame = null;
            place();
          });
        };
        this.observer = new MutationObserver(schedulePlace);
        this.observer.observe(gallery, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
        this.resize = new ResizeObserver(schedulePlace);
        this.resize.observe(gallery);
        place();
      }
      if (type === 'qual' && 'IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) start();
        }, { rootMargin: '300px 0px' });
        this.intersection.observe(this);
        // Fetch just before arrival, but animate only when actually visible.
        this.reveal = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.classList.add('is-visible');
          this.reveal.disconnect();
        }, { threshold: 0.1 });
        this.reveal.observe(this);
      } else start();
    }
    disconnectedCallback() {
      // append() also disconnects during positioning; only clean up real removals.
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.observer?.disconnect();
        this.resize?.disconnect();
        cancelAnimationFrame(this.positionFrame);
        this.intersection?.disconnect();
        this.reveal?.disconnect();
        clearTimeout(this.loadingTimer);
        if (this.onMessage) window.removeEventListener('message', this.onMessage);
      });
    }
  }
  customElements.define('frontrow-widget', FrontrowWidget);
})();
